// Vue modules
import { Preferences } from '@capacitor/preferences';
import { isReactive, reactive } from 'vue';

import SessionManager from '../classes/session.manager.js';

// AsyncLocalStorage для изоляции SSR store per-request (только Node.js)
let asyncLocalStorage = null;

// Импорт async_hooks ленивый и под проверкой window: файл собирается и для
// браузера, статический импорт узлового модуля туда тащить нельзя.
// Создание идёт одним полётом через сохранённый промис. Замер 2026-09-07:
// на холодном процессе два одновременных SSR-запроса оба видели null, оба
// доходили до await import и создавали по своему экземпляру AsyncLocalStorage.
// Запрос, начавший als.run на первом экземпляре, терял контекст — useStore()
// читал переменную модуля, где уже лежал второй.
let async_local_storage_ready = null;

export async function getAsyncLocalStorage() {
  if (asyncLocalStorage || typeof window !== 'undefined') {
    return asyncLocalStorage;
  }
  if (!async_local_storage_ready) {
    async_local_storage_ready = import('async_hooks').then(({ AsyncLocalStorage }) => {
      if (!asyncLocalStorage) {
        asyncLocalStorage = new AsyncLocalStorage();
      }
      return asyncLocalStorage;
    });
  }
  return async_local_storage_ready;
}

// ============================================================================
// GLOBAL FUNCTIONS (используют useStore() для доступа к state)
// ============================================================================

// Готовый текст ошибки. Код ошибки переводится здесь по ключу core.errors.<CODE>:
// инстанс vue-i18n свой на каждый SSR-запрос и лежит в контексте запроса
// (store.context.i18n), поэтому берём его оттуда, а не из глобального инстанса.
// Перевода нет — показываем сам код.
// Пара к setError: getError отдаёт текст для показа по месту (inline в форме,
// empty state), setError показывает его в глобальной плашке.
export function getError(error) {
  let errorData;

  errorData = error;

  // Обработка ошибок из fetch API (Store class)
  if (error?.info) errorData = error.info;

  // Обработка ошибок из axios
  if (error?.response?.data) errorData = error.response.data;

  // Обработка ошибок верификации
  if ((errorData?.errorCode === 'VALIDATION_ERROR' || errorData?.errors) && errorData?.errors) {
    // Собираем все сообщения об ошибках в одну строку
    const errorMessages = [];
    for (const field in errorData.errors) {
      const fieldErrors = errorData.errors[field];
      if (Array.isArray(fieldErrors)) {
        errorMessages.push(...fieldErrors);
      } else if (typeof fieldErrors === 'string') {
        errorMessages.push(fieldErrors);
      }
    }
    return errorMessages.join(', ') || errorData.message || 'Validation error';
  }

  if (errorData?.errorCode) {
    const i18n = useStore().context?.i18n;
    return i18n?.global.te(`core.errors.${errorData.errorCode}`) ? i18n.global.t(`core.errors.${errorData.errorCode}`) : errorData.errorCode;
  }

  return errorData?.message || error?.message || 'Unknown error';
}

// Mutations
export function setError(error) {
  const state = useStore().core.state;
  state.error.message = getError(error);

  state.error.show = true;

  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    requestAnimationFrame(() => {
      setTimeout(() => (state.error.show = false), 5000);
    });
  } else {
    setTimeout(() => (state.error.show = false), 5000);
  }

  return state.error;
}

export function setSnack(data) {
  const state = useStore().core.state;

  // Handle different input formats
  let type = 'notification'
  let message = ''
  let duration = 3000

  if (typeof data === 'string') {
    message = data
  } else if (data instanceof Error) {
    type = 'error'
    message = data.message
  } else if (data?.response?.data) {
    // Handle API errors
    type = 'error'
    const errorData = data.response.data
    message = errorData.errorCode || errorData.message || 'Unknown error'
  } else if (typeof data === 'object') {
    type = data.type || 'notification'
    message = data.message || ''
    duration = data.duration || 3000
  }

  // Update state
  state.snack = {
    show: true,
    type,
    message,
    duration
  }

  // Auto-hide
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    requestAnimationFrame(() => {
      setTimeout(() => {state.snack.show = false }, duration)
    });
  } else {
    setTimeout(() => {state.snack.show = false }, duration)
  }
}

// ============================================================================
// МОДУЛЬ-СТОРЫ
// ============================================================================

// Фабрики модуль-сторов: имя → factory(). Регистр один на процесс, экземпляр
// стора — на запрос (SSR-изоляция), поэтому фабрика хранится здесь, а экземпляры
// живут на сторе запроса.
const store_factories = new Map();

// Объявление модуль-стора: `export const searches = defineStore('searches', () => {
// const state = reactive({...}); async function loadList() {...}; return { state, loadList }; })`.
// Константа одна на процесс, экземпляр — на запрос: поиск экземпляра стоит в
// одном месте ядра (как reactive у Vue), потребителю достаточно константы.
// Экземпляр создаётся при первом обращении к любому ключу; второй вызов фабрики
// в том же запросе невозможен — наличие store[name] проверяется до вызова.
export function defineStore(name, factory) {
  store_factories.set(name, factory);

  const instance = () => {
    const store = useStore();
    if (!store[name]) store.addStore(name, factory());
    return store[name];
  };

  return new Proxy(
    {},
    {
      get: (_, key) => Reflect.get(instance(), key),
      set: (_, key, value) => Reflect.set(instance(), key, value),
      has: (_, key) => Reflect.has(instance(), key),
      deleteProperty: (_, key) => Reflect.deleteProperty(instance(), key),
      ownKeys: () => Reflect.ownKeys(instance()),
      getOwnPropertyDescriptor: (_, key) => {
        const descriptor = Reflect.getOwnPropertyDescriptor(instance(), key);
        return descriptor ? { ...descriptor, configurable: true } : undefined;
      },
    }
  );
}

// ============================================================================
// GLOBAL STORE FACTORY
// ============================================================================

// Фабрика для создания store
export function createStore() {
  // State создается для каждого store instance (изоляция SSR)
  const state = reactive({
    loading: false,
    notFound: false,
    // Клиентское приложение смонтировано (гидрация SSR закончена). Пока false,
    // компоненты рисуют то, что пришло с сервера в data-state; после — данные
    // в сторе могут остаться от предыдущей страницы (переход внутри SPA), и
    // компонент обязан читать свои заново. Ставит vue-app-renderer после mount.
    hydrated: false,

    isOpenLocationPopup: false,
    isOpenSidebar: false,
    // Пользователь свернул сайдбар руками. Пока флаг стоит, автораскрытие по
    // route.meta.sidebarOpenOnEnter не срабатывает: смена раздела не должна
    // отменять явное решение свернуть. Снимается ручным раскрытием.
    isSidebarClosedByUser: false,

    position: null,
    search: null,

    // Слот значения темы. УПРАВЛЕНИЕ — в дизайн-системе: ds.theme.set/toggle/init
    // (@martyrs/src/design-system/utils/theme.js). Здесь только хранение — ради
    // per-request SSR-изоляции и гидрации, которые даёт механика этого стора.
    theme: {
      mode: 'system',   // 'light' | 'dark' | 'system'
    },

    // Действия шапки, которые вносят модули (кнопка корзины orders): модуль
    // добавляет запись в своём initialize, ядро про сами действия не знает.
    // Запись: { component, order }, рендер по возрастанию order.
    header: {
      actions: [],
    },

    navigation_bar: {
      name: null,
      actions: null,
    },

    error: {
      status: '',
      headers: '',
      data: '',
      show: false,
      name: '',
      // Готовый текст ошибки (getError уже перевёл код).
      message: '',
    },
    snack: {
      show: false,
      type: 'notification',
      message: '',
      duration: 3000
    },

    // Session state - данные сессии хранятся здесь
    session: {
      token: null,
      userId: null,
      roles: null,
      accesses: []
    },
  });

  // Actions с closure на локальный state
  const actions = {
    setLoading(status) {
      state.loading = status;
    },

    // setTheme/toggleTheme ПЕРЕЕХАЛИ в дизайн-систему:
    // import { ds } from '@martyrs/src/design-system/index.js' → ds.theme.set()/toggle()

    add(array, item) {
      const existingItemIndex = array.findIndex(i => i._id === item._id);

      if (existingItemIndex === -1) {
        array.push(item);
      } else {
        array[existingItemIndex] = item;
      }
    },

    update(array, item) {
      const existingItemIndex = array.findIndex(i => i._id === item._id);
      if (existingItemIndex === -1) {
        // If the item doesn't exist, push it to the array
        array.push(item);
      } else {
        // Update the item in the array without creating a new array
        Object.assign(array[existingItemIndex], item);
      }
    },

    delete(array, item) {
      const existingItemIndex = array.findIndex(c => c._id === item._id);

      if (existingItemIndex !== -1) {
        array.splice(existingItemIndex, 1);
      }
    },

    increment(array, item) {
      console.log(array);
      console.log(item);
      const arrayItem = array.find(i => i._id === item._id);

      if (arrayItem) {
        arrayItem.quantity++;
      }
    },

    decrement(array, item) {
      const arrayItem = array.find(i => i._id === item._id);

      const arrayItemIndex = array.indexOf(arrayItem);

      if (arrayItemIndex > -1) {
        arrayItem.quantity--;

        if (arrayItem.quantity < 1) array.splice(arrayItemIndex, 1);
      }
    },

    reset(array) {
      array = [];
    },
  };

  // SessionManager создается внутри createStore для правильной изоляции в SSR
  const sessionManager = new SessionManager(state.session);

  const store = reactive({
    modules: [],
    // Модули, активированные в этом запросе (initialize + локаль). Живёт на сторе,
    // а не в module.manager: стор per-request на SSR (AsyncLocalStorage), поэтому
    // параллельные запросы не считают чужую активацию своей.
    activated: [],

    // Core module registered by default
    core: { state, actions, session: sessionManager },

    addStore(name, storage) {
      // storage — экземпляр модуль-стора этого запроса (его создаёт фабрика из
      // defineStore) либо класс-стор с внешним state.
      this[name] = storage;
      this.modules.push(name);
    },

    async getInitialState() {
      const service = ['modules', 'activated', 'context', 'addStore', 'getInitialState', 'setInitialState'];
      const initialState = {};
      for (const [moduleName, moduleStore] of Object.entries(this)) {
        if (!service.includes(moduleName)) {
          if (moduleStore.state) {
            initialState[moduleName] = JSON.parse(JSON.stringify(moduleStore.state));
          }
        }
      }
      return initialState;
    },

    async setInitialState(initialState, isHydration = false) {
      const modules = Object.entries(initialState);

      for (const [moduleName, moduleState] of modules) {
        // Экземпляра стора в этом запросе ещё нет, а фабрика объявлена (файл
        // стора импортирован при активации модулей сервера) — создаём его, чтобы
        // сериализованный сервером state было куда положить. Так гидратация не
        // требует активировать модуль ради одного стора.
        if (!this[moduleName] && store_factories.has(moduleName)) {
          this.addStore(moduleName, store_factories.get(moduleName)());
        }

        if (this[moduleName] && this[moduleName].state) {
          // При гидратации просто заменяем state целиком для скорости
          if (isHydration) {
            Object.assign(this[moduleName].state, moduleState);
          } else {
            mergeReactive(this[moduleName].state, moduleState);
          }
        }
      }
    }
  });

  function mergeReactive(target, source) {
    // Оптимизированная версия слияния
    const keys = Object.keys(source);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const sourceValue = source[key];

      if (sourceValue !== null && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        if (isReactive(target[key])) {
          mergeReactive(target[key], sourceValue);
        } else {
          target[key] = sourceValue;
        }
      } else {
        target[key] = sourceValue;
      }
    }
  }

  return store;
}

// ============================================================================
// SSR AND SINGLETON
// ============================================================================

// Синглтон для клиента
let clientStore = null;

export function useStore() {
  if (typeof window === 'undefined') {
    // SSR: стор запроса лежит в AsyncLocalStorage.
    // Пустой контекст — это ошибка порядка инициализации, а не повод создать
    // новый стор: тихий фолбэк 2026-09-07 отдал состояние стору-сироте, и
    // страница рендерилась без авторизации. Ошибка вместо фолбэка.
    const store = asyncLocalStorage?.getStore();
    if (store) {
      return store;
    }
    throw new Error(
      'useStore(): обращение к стору вне контекста запроса. Стор запроса создаёт renderApp в core.app.js и входит в него через als.run — код, который зовёт useStore до als.run, надо перенести внутрь контекста запроса.'
    );
  }
  // Client: синглтон
  if (!clientStore) {
    clientStore = createStore();
  }
  return clientStore;
}

// ============================================================================
// SESSION ACCESSOR
// ============================================================================

/**
 * Получить текущий экземпляр SessionManager
 * Использовать вместо прямого импорта session для правильной работы с SSR
 */
export function useSession() {
  const store = useStore();
  return store.core.session;
}

// ============================================================================
// EXPORTS
// ============================================================================

// Дефолтного экспорта нет: он вычислялся на импорте модуля, то есть вне контекста
// запроса, и создавал стор-сироту. Стор берётся только через useStore().
